import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, ChevronRight, ChevronDown, Plus, CheckCircle2, XCircle, Clock, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api, apiErrorText } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKKPICard, VLKButton, VLKInput, VLKSelect,
  VLKTextarea, VLKLabel, VLKAlertDialog, VLKEmptyState, toast,
} from "@/components/vlk";

const SECTORS = [
  { value: "government", label: "Government agency" },
  { value: "financial", label: "Financial institution" },
  { value: "healthcare", label: "Healthcare organization" },
  { value: "university", label: "University" },
  { value: "enterprise", label: "Enterprise" },
  { value: "other", label: "Other" },
];

const STAGE_LABELS = {
  INVITATION_SENT: "Invited",
  INSTITUTION_REGISTRATION: "Registered",
  ORGANIZATION_PROFILE: "Profile",
  PRIMARY_ADMIN_ASSIGNED: "Admin assigned",
  ADMIN_IDENTITY_VERIFICATION: "Admin verified",
  LEGAL_DOCS_UPLOADED: "Legal docs",
  COMPLIANCE_REVIEW: "Compliance review",
  APPROVAL: "Approved",
  SANDBOX_PROVISIONED: "Sandbox provisioned",
  API_CREDENTIALS_ISSUED: "Credentials issued",
  DEVELOPER_PORTAL_ACCESS: "Dev portal access",
  TESTING_PHASE: "Testing",
  PRODUCTION_APPROVAL: "Production review",
  PRODUCTION_ACTIVE: "Production live",
};

const STATUS_STYLES = {
  IN_PROGRESS: "text-info border-info/30 bg-info/5",
  COMPLETE:    "text-success border-success/30 bg-success/5",
  REJECTED:    "text-destructive border-destructive/30 bg-destructive/5",
};

export default function Onboarding() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [openId, setOpenId] = useState(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["onboardings"],
    queryFn: () => api.get("/onboarding").then((r) => r.data),
  });

  const items = data?.items || [];
  const kpis = useMemo(() => {
    const s = { total: items.length, in_progress: 0, complete: 0, rejected: 0, in_compliance: 0 };
    for (const o of items) {
      if (o.status === "IN_PROGRESS") s.in_progress += 1;
      if (o.status === "COMPLETE") s.complete += 1;
      if (o.status === "REJECTED") s.rejected += 1;
      if (o.stage === "COMPLIANCE_REVIEW") s.in_compliance += 1;
    }
    return s;
  }, [items]);

  return (
    <div data-testid="onboarding-page">
      <VLKPageHeader
        title="Institution onboarding"
        description="14-stage guided workflow for government agencies, financial institutions, healthcare organizations, universities, and enterprise customers."
        actions={
          <VLKButton size="md" data-testid="onboarding-invite-btn" onClick={() => setShowInvite(true)}>
            <Plus className="h-4 w-4" /> Invite institution
          </VLKButton>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <VLKKPICard testId="onb-kpi-total" label="Onboardings" value={kpis.total} icon={Building2} />
        <VLKKPICard testId="onb-kpi-progress" label="In progress" value={kpis.in_progress} tone="info" icon={Clock} />
        <VLKKPICard testId="onb-kpi-compliance" label="Awaiting compliance" value={kpis.in_compliance} tone="warning" icon={Clock} />
        <VLKKPICard testId="onb-kpi-complete" label="Production live" value={kpis.complete} tone="success" icon={CheckCircle2} />
      </div>

      <VLKCard>
        {isLoading ? (
          <div className="p-10 text-muted-foreground text-sm flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading onboardings…
          </div>
        ) : items.length === 0 ? (
          <VLKEmptyState icon={Building2} title="No onboardings yet" description="Send your first invitation to start the guided workflow." />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((o) => (
              <OnboardingRow key={o.onboarding_id} onb={o} open={openId === o.onboarding_id}
                              onToggle={() => setOpenId((v) => v === o.onboarding_id ? null : o.onboarding_id)}
                              onChanged={() => { refetch(); qc.invalidateQueries({ queryKey: ["onboardings"] }); }} />
            ))}
          </ul>
        )}
      </VLKCard>

      {showInvite && <InviteDialog onClose={() => setShowInvite(false)} onCreated={() => { refetch(); }} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function OnboardingRow({ onb, open, onToggle, onChanged }) {
  const stageIdx = Object.keys(STAGE_LABELS).indexOf(onb.stage);
  const totalStages = Object.keys(STAGE_LABELS).length;
  const progress = onb.status === "COMPLETE" ? 100 : onb.status === "REJECTED" ? 0 : Math.max(6, Math.round(((stageIdx + 1) / totalStages) * 100));

  return (
    <li data-testid={`onboarding-row-${onb.onboarding_id}`}>
      <button className="w-full text-left px-4 py-3 hover:bg-secondary/40 transition-colors" onClick={onToggle}>
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-medium">{onb.institution_name}</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{onb.sector}</span>
              <span className="font-mono text-xs text-muted-foreground">{onb.admin_email}</span>
              <span className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-wider", STATUS_STYLES[onb.status])}>
                {onb.status.replace("_", " ")}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{STAGE_LABELS[onb.stage] || onb.stage}</span>
            </div>
            <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full transition-all",
                  onb.status === "REJECTED" ? "bg-destructive" : onb.status === "COMPLETE" ? "bg-success" : "bg-foreground"
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </button>
      {open && <OnboardingWizard onb={onb} onChanged={onChanged} />}
    </li>
  );
}

/* ------------------------------------------------------------------ */

function OnboardingWizard({ onb, onChanged }) {
  const qc = useQueryClient();
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["onboardings"] }); onChanged?.(); };
  return (
    <div className="px-6 pb-6 pt-2 space-y-3 bg-secondary/10 border-t border-border">
      {/* Timeline */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] font-mono">
        {Object.entries(STAGE_LABELS).map(([stage, label], i) => {
          const idx = Object.keys(STAGE_LABELS).indexOf(onb.stage);
          const reached = i <= idx;
          const rejected = onb.status === "REJECTED";
          return (
            <div key={stage} className={cn(
              "px-2 py-1 rounded-sm border truncate",
              rejected ? "border-border text-muted-foreground" :
              reached ? "border-foreground text-foreground" : "border-border text-muted-foreground opacity-60"
            )}>
              {i + 1}. {label}
            </div>
          );
        })}
      </div>

      {/* Step form for current stage */}
      <StageForm onb={onb} onDone={invalidate} />

      {/* Activity trail */}
      <details className="mt-3">
        <summary className="text-xs cursor-pointer text-muted-foreground uppercase tracking-widest">Activity ({(onb.activity || []).length})</summary>
        <ol className="mt-2 relative border-l border-border ml-2">
          {(onb.activity || []).slice().reverse().map((a, i) => (
            <li key={`${a.timestamp || i}-${a.kind}`} className="pl-4 py-1 text-xs">
              <span className="text-muted-foreground font-mono">{new Date(a.timestamp).toLocaleString()}</span>
              <span className="ml-2 uppercase tracking-widest text-[10px] text-muted-foreground">{a.kind}</span>
              <span className="ml-2">{a.note || `${a.from_stage || "-"} → ${a.to_stage || "-"}`}</span>
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function StageForm({ onb, onDone }) {
  const stage = onb.stage;
  if (onb.status === "REJECTED") {
    return <div className="text-sm text-destructive">Rejected — {onb.rejection_reason || "no reason recorded"}</div>;
  }
  if (stage === "PRODUCTION_ACTIVE") {
    return <div className="text-sm text-success">Production live · sandbox {onb.sandbox_credential_id} · production {onb.production_credential_id}</div>;
  }
  if (stage === "INVITATION_SENT") return <RegistrationForm onb={onb} onDone={onDone} />;
  if (stage === "INSTITUTION_REGISTRATION") return <ProfileForm onb={onb} onDone={onDone} />;
  if (stage === "ORGANIZATION_PROFILE") return <AdminForm onb={onb} onDone={onDone} />;
  if (stage === "PRIMARY_ADMIN_ASSIGNED") return <VerificationForm onb={onb} onDone={onDone} />;
  if (stage === "ADMIN_IDENTITY_VERIFICATION") return <LegalDocsForm onb={onb} onDone={onDone} />;
  if (stage === "LEGAL_DOCS_UPLOADED") return <div className="text-sm text-muted-foreground">Moving to compliance review…</div>;
  if (stage === "COMPLIANCE_REVIEW") return <ComplianceDecisionForm onb={onb} onDone={onDone} />;
  if (["APPROVAL", "SANDBOX_PROVISIONED", "API_CREDENTIALS_ISSUED", "DEVELOPER_PORTAL_ACCESS"].includes(stage)) {
    return <div className="text-sm text-muted-foreground">Auto-provisioning · will advance to Testing…</div>;
  }
  if (stage === "TESTING_PHASE") return <TestingForm onb={onb} onDone={onDone} />;
  if (stage === "PRODUCTION_APPROVAL") return <ProductionDecisionForm onb={onb} onDone={onDone} />;
  return null;
}

function InviteDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ institution_name: "", admin_email: "", country: "", sector: "financial", message: "" });
  const mut = useMutation({
    mutationFn: () => api.post("/onboarding/invite", form).then((r) => r.data),
    onSuccess: (o) => { toast.success("Invitation dispatched", { description: o.onboarding_id }); onCreated?.(); onClose(); },
    onError: (e) => toast.error("Invite failed", { description: apiErrorText(e) }),
  });
  return (
    <VLKAlertDialog open onOpenChange={(v) => !v && onClose()} title="Invite institution"
      actions={<>
        <VLKButton variant="ghost" onClick={onClose}>Cancel</VLKButton>
        <VLKButton data-testid="onboarding-invite-submit" loading={mut.isPending}
                   disabled={!form.institution_name || !form.admin_email || !form.country}
                   onClick={() => mut.mutate()}>Send invitation</VLKButton>
      </>}>
      <div className="space-y-3">
        <div><VLKLabel>Institution name</VLKLabel><VLKInput data-testid="onb-invite-name" value={form.institution_name} onChange={(e) => setForm((f) => ({ ...f, institution_name: e.target.value }))} /></div>
        <div><VLKLabel>Admin email</VLKLabel><VLKInput data-testid="onb-invite-email" value={form.admin_email} onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))} placeholder="director@example.gov" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><VLKLabel>Country</VLKLabel><VLKInput data-testid="onb-invite-country" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} placeholder="US" /></div>
          <div><VLKLabel>Sector</VLKLabel>
            <VLKSelect data-testid="onb-invite-sector" value={form.sector} onChange={(e) => setForm((f) => ({ ...f, sector: e.target.value }))}>
              {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </VLKSelect>
          </div>
        </div>
        <div><VLKLabel>Custom message (optional)</VLKLabel><VLKTextarea rows={3} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} /></div>
      </div>
    </VLKAlertDialog>
  );
}

function useStageMutation(path, onDone, label) {
  return useMutation({
    mutationFn: (payload) => api.post(path, payload).then((r) => r.data),
    onSuccess: () => { toast.success(label); onDone?.(); },
    onError: (e) => toast.error(`${label} failed`, { description: apiErrorText(e) }),
  });
}

function RegistrationForm({ onb, onDone }) {
  const [f, setF] = useState({ legal_name: onb.institution_name || "", registration_number: "", tax_id: "", address: "" });
  const mut = useStageMutation(`/onboarding/${onb.onboarding_id}/register`, onDone, "Registration submitted");
  return (
    <StepShell title="Institution registration" testid="stage-register" onSubmit={() => mut.mutate(f)} disabled={!f.legal_name || !f.registration_number || !f.address || mut.isPending} loading={mut.isPending}>
      <TwoCol><VLKLabel>Legal name</VLKLabel><VLKInput value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} /></TwoCol>
      <TwoCol><VLKLabel>Registration number</VLKLabel><VLKInput value={f.registration_number} onChange={(e) => setF({ ...f, registration_number: e.target.value })} /></TwoCol>
      <TwoCol><VLKLabel>Tax ID (optional)</VLKLabel><VLKInput value={f.tax_id} onChange={(e) => setF({ ...f, tax_id: e.target.value })} /></TwoCol>
      <TwoCol className="col-span-2"><VLKLabel>Registered address</VLKLabel><VLKTextarea rows={2} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></TwoCol>
    </StepShell>
  );
}

function ProfileForm({ onb, onDone }) {
  const [f, setF] = useState({ description: "", website: "", support_email: "", logo_url: "" });
  const mut = useStageMutation(`/onboarding/${onb.onboarding_id}/profile`, onDone, "Profile saved");
  return (
    <StepShell title="Organization profile" testid="stage-profile" onSubmit={() => mut.mutate(f)} disabled={f.description.length < 3 || mut.isPending} loading={mut.isPending}>
      <TwoCol className="col-span-2"><VLKLabel>Description</VLKLabel><VLKTextarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></TwoCol>
      <TwoCol><VLKLabel>Website</VLKLabel><VLKInput value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="https://..." /></TwoCol>
      <TwoCol><VLKLabel>Support email</VLKLabel><VLKInput value={f.support_email} onChange={(e) => setF({ ...f, support_email: e.target.value })} /></TwoCol>
    </StepShell>
  );
}

function AdminForm({ onb, onDone }) {
  const [f, setF] = useState({ admin_name: "", admin_email: onb.admin_email || "", admin_phone: "", admin_role_title: "" });
  const mut = useStageMutation(`/onboarding/${onb.onboarding_id}/admin`, onDone, "Primary admin assigned");
  return (
    <StepShell title="Primary administrator" testid="stage-admin" onSubmit={() => mut.mutate(f)} disabled={!f.admin_name || !f.admin_email || mut.isPending} loading={mut.isPending}>
      <TwoCol><VLKLabel>Full name</VLKLabel><VLKInput value={f.admin_name} onChange={(e) => setF({ ...f, admin_name: e.target.value })} /></TwoCol>
      <TwoCol><VLKLabel>Email</VLKLabel><VLKInput value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} /></TwoCol>
      <TwoCol><VLKLabel>Phone</VLKLabel><VLKInput value={f.admin_phone} onChange={(e) => setF({ ...f, admin_phone: e.target.value })} /></TwoCol>
      <TwoCol><VLKLabel>Role title</VLKLabel><VLKInput value={f.admin_role_title} onChange={(e) => setF({ ...f, admin_role_title: e.target.value })} /></TwoCol>
    </StepShell>
  );
}

function VerificationForm({ onb, onDone }) {
  const [f, setF] = useState({ verified_document_ref: "", verification_kind: "passport" });
  const mut = useStageMutation(`/onboarding/${onb.onboarding_id}/verify-admin`, onDone, "Admin verified");
  return (
    <StepShell title="Admin identity verification" testid="stage-verify" onSubmit={() => mut.mutate(f)} disabled={!f.verified_document_ref || mut.isPending} loading={mut.isPending}>
      <TwoCol><VLKLabel>Document reference</VLKLabel><VLKInput value={f.verified_document_ref} onChange={(e) => setF({ ...f, verified_document_ref: e.target.value })} placeholder="doc://..." /></TwoCol>
      <TwoCol><VLKLabel>Verification kind</VLKLabel>
        <VLKSelect value={f.verification_kind} onChange={(e) => setF({ ...f, verification_kind: e.target.value })}>
          <option value="passport">Passport</option>
          <option value="national_id">National ID</option>
          <option value="corporate_directors_certificate">Directors certificate</option>
          <option value="other">Other</option>
        </VLKSelect>
      </TwoCol>
    </StepShell>
  );
}

function LegalDocsForm({ onb, onDone }) {
  const [docs, setDocs] = useState([{ _rid: Math.random().toString(36).slice(2), kind: "articles_of_incorporation", reference: "", description: "" }]);
  const mut = useStageMutation(`/onboarding/${onb.onboarding_id}/legal-docs`, onDone, "Legal documents uploaded");
  const ready = docs.every((d) => d.reference && d.description);
  return (
    <StepShell title="Legal documentation" testid="stage-legal" onSubmit={() => mut.mutate({ docs: docs.map(({ _rid, ...rest }) => rest) })} disabled={!ready || mut.isPending} loading={mut.isPending}>
      <div className="col-span-2 space-y-2">
        {docs.map((d, i) => (
          <div key={d._rid} className="grid grid-cols-[160px_1fr_1fr] gap-2 items-end">
            <div><VLKLabel>Kind</VLKLabel>
              <VLKSelect value={d.kind} onChange={(e) => setDocs(docs.map((x, j) => j === i ? { ...x, kind: e.target.value } : x))}>
                <option value="articles_of_incorporation">Articles of incorporation</option>
                <option value="regulatory_licence">Regulatory licence</option>
                <option value="beneficial_owner">Beneficial owner</option>
                <option value="power_of_attorney">Power of attorney</option>
                <option value="other">Other</option>
              </VLKSelect>
            </div>
            <div><VLKLabel>Reference</VLKLabel><VLKInput value={d.reference} onChange={(e) => setDocs(docs.map((x, j) => j === i ? { ...x, reference: e.target.value } : x))} placeholder="doc://..." /></div>
            <div><VLKLabel>Description</VLKLabel><VLKInput value={d.description} onChange={(e) => setDocs(docs.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} /></div>
          </div>
        ))}
        <VLKButton variant="ghost" size="sm" type="button" onClick={() => setDocs([...docs, { _rid: Math.random().toString(36).slice(2), kind: "other", reference: "", description: "" }])}>+ Add document</VLKButton>
      </div>
    </StepShell>
  );
}

function ComplianceDecisionForm({ onb, onDone }) {
  const [decision, setDecision] = useState("APPROVE");
  const [note, setNote] = useState("");
  const mut = useStageMutation(`/onboarding/${onb.onboarding_id}/compliance-decision`, onDone, "Compliance decision recorded");
  return (
    <StepShell title="Compliance review" testid="stage-compliance" onSubmit={() => mut.mutate({ decision, note })} disabled={note.length < 3 || mut.isPending} loading={mut.isPending}>
      <TwoCol><VLKLabel>Decision</VLKLabel>
        <VLKSelect value={decision} onChange={(e) => setDecision(e.target.value)}>
          <option value="APPROVE">Approve — provision sandbox</option>
          <option value="REJECT">Reject — halt onboarding</option>
        </VLKSelect>
      </TwoCol>
      <TwoCol className="col-span-2"><VLKLabel>Note (regulator-visible)</VLKLabel><VLKTextarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></TwoCol>
    </StepShell>
  );
}

function TestingForm({ onb, onDone }) {
  const [f, setF] = useState({ verifications_completed: 0, ready_for_production: false });
  const mut = useStageMutation(`/onboarding/${onb.onboarding_id}/testing-report`, onDone, "Testing report saved");
  return (
    <StepShell title="Testing phase" testid="stage-testing" onSubmit={() => mut.mutate(f)} disabled={mut.isPending} loading={mut.isPending}>
      <TwoCol><VLKLabel>Verifications completed</VLKLabel><VLKInput type="number" value={f.verifications_completed} onChange={(e) => setF({ ...f, verifications_completed: Number(e.target.value) })} /></TwoCol>
      <TwoCol className="col-span-2 flex items-center gap-2 pt-6">
        <input id="ready" type="checkbox" checked={f.ready_for_production} onChange={(e) => setF({ ...f, ready_for_production: e.target.checked })} />
        <label htmlFor="ready" className="text-sm">Mark as ready for production approval</label>
      </TwoCol>
    </StepShell>
  );
}

function ProductionDecisionForm({ onb, onDone }) {
  const [decision, setDecision] = useState("APPROVE");
  const [note, setNote] = useState("");
  const mut = useStageMutation(`/onboarding/${onb.onboarding_id}/production-decision`, onDone, "Production decision recorded");
  return (
    <StepShell title="Production approval" testid="stage-production" onSubmit={() => mut.mutate({ decision, note })} disabled={note.length < 3 || mut.isPending} loading={mut.isPending}>
      <TwoCol><VLKLabel>Decision</VLKLabel>
        <VLKSelect value={decision} onChange={(e) => setDecision(e.target.value)}>
          <option value="APPROVE">Approve — activate production</option>
          <option value="REJECT">Reject — hold in testing</option>
        </VLKSelect>
      </TwoCol>
      <TwoCol className="col-span-2"><VLKLabel>Note</VLKLabel><VLKTextarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></TwoCol>
    </StepShell>
  );
}

/* -------------------- Layout helpers -------------------- */

function StepShell({ title, testid, children, onSubmit, disabled, loading }) {
  return (
    <div data-testid={testid} className="border border-border rounded-sm bg-card p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
      <div className="mt-4">
        <VLKButton data-testid={`${testid}-submit`} loading={loading} disabled={disabled} onClick={onSubmit}>
          Advance stage
        </VLKButton>
      </div>
    </div>
  );
}

function TwoCol({ children, className }) {
  return <div className={cn(className)}>{children}</div>;
}
